import React, { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { LogChangeType, logFieldValue } from "@/entities/history/types";
import { toIdList } from "@/entities/history/helpers";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { APIFieldItem } from "@/pages/settings/types";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { ArrowRight } from "@dreamhub/designsystem/icons";
import HighlightedText from "@/components/controls/HighlightedText";
import CompanyName from "@/components/layouts/History/company/CompanyName";
import TextField from "@/components/layouts/History/TextField";
import { simpleDate, toLocalDate } from "@/utils/datetime";
import Stage from "@/components/layouts/History/deal/Stage";
import EntityStatus from "@/components/layouts/History/EntityStatus";
import IndustryName from "@/components/layouts/History/company/IndustryName";
import SegmentationName from "@/components/layouts/History/company/SegmentationName";
import JobFunctionName from "@/components/layouts/History/person/jobFunctionName";
import ProductName from "@/components/layouts/History/ProductName";
import LicenseTypeName from "@/components/layouts/History/lead/LicenseTypeName";
import SourceName from "@/components/layouts/History/lead/SourceName";
import SourceTypeName from "@/components/layouts/History/lead/SourceTypeName";
import Referenceability from "@/components/layouts/History/person/Referenceability";
import AEConfidenceLevel from "@/components/layouts/History/deal/AEConfidenceLevel";
import DealValueAmount from "@/components/layouts/History/deal/DealValueAmount";
import PeopleDelta from "@/components/layouts/History/PeopleDelta";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { useSearchHighlight } from "@/contexts/SearchHighlightContext";
import Currency from "@/components/layouts/History/deal/Currency";
import { getSpecialPersonDisplayValue } from "@/entities/people/helpers";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import SubIndustryName from "@/components/layouts/History/company/SubIndustryName";
import RenewalTypeName from "@/components/layouts/History/contract/RenewalTypeName";
import { OnboardingStageName } from "@/components/layouts/History/contract/OnboardingStageName";
import UserOrPersonBadge from "@/components/layouts/Activities/ActivityDetails/UserOrPersonBadge";
import PartnerName from "@/components/layouts/History/partner/PartnerName";
import PartnerContactName from "@/components/layouts/History/partner/PartnerContactName";
import { getEnumFieldConfig } from "@/components/layouts/History/enumFieldConfig";
import { getEnumTKey } from "@/utils/strings";
import { parseOptions } from "@/utils/entityFieldsUtils/shared";

interface LogActionProps {
  entityType: EntityType;
  changeType?: LogChangeType;
  fieldName: string;
  delta: logFieldValue[];
  peopleIdsDelta: string[];
  isSingleChange: boolean;
  fieldConfig?: APIFieldItem;
}

const LogAction: React.FC<LogActionProps> = React.memo(
  ({
    entityType,
    changeType,
    fieldName,
    delta,
    peopleIdsDelta,
    isSingleChange,
    fieldConfig
  }) => {
    const { searchQuery } = useSearchHighlight();
    const { t } = useTranslation();

    const { fieldName: arrOrMrrField } = useArrMrr();

    const showPeopleDelta =
      peopleIdsDelta?.length > 0 && fieldName === "manuallyAddedPeople";

    const renderActionValue = useCallback(
      (value: logFieldValue): ReactNode => {
        // Fields that have special person IDs as valid values
        if (fieldName === "reportsTo") {
          const specialDisplayValue = getSpecialPersonDisplayValue(
            value?.toString(),
            t
          );
          if (specialDisplayValue) {
            return <TextField>{specialDisplayValue}</TextField>;
          }
          return (
            <UserOrPersonBadge
              id={value as string}
              className="bg-transparent"
              textVariant="bodySecondary"
            />
          );
        }
        // A system select backed by a lookup table with no FK names that table in
        // config, and settingsapi resolves its rows into config.options.
        if (fieldConfig?.config?.optionsSourceTable) {
          const optionIds = toIdList(value);
          if (optionIds.length === 0)
            return <TextField>{t("unknown")}</TextField>;
          const optionNames = new Map(
            parseOptions(fieldConfig.config).map(({ id, displayName }) => [
              String(id),
              displayName
            ])
          );
          return (
            <TextField>
              {optionIds
                .map((id) => optionNames.get(String(id)) || t("notFound"))
                .join(", ")}
            </TextField>
          );
        }
        const enumConfig = getEnumFieldConfig(entityType, fieldName);
        if (enumConfig) {
          const enumIds = toIdList(value);
          if (enumIds.length === 0)
            return <TextField>{t("unknown")}</TextField>;
          return (
            <TextField>
              {enumIds
                .map((enumId) =>
                  t(
                    getEnumTKey(
                      enumConfig.namespace,
                      enumConfig.subKey,
                      enumConfig.enumType,
                      Number(enumId)
                    )
                  )
                )
                .join(", ")}
            </TextField>
          );
        }
        if (value === null) return <TextField>{t("unknown")}</TextField>;
        switch (fieldName) {
          case "status":
            // Revenue item statuses use a different enum than their parent
            // entity — fall through to the plain-text default
            if (changeType === LogChangeType.REVENUE_ITEMS) break;
            return (
              <EntityStatus value={value as string} entityType={entityType} />
            );
          case "stage":
            return <Stage value={value as string} />;
          case "industryId":
            return <IndustryName value={value as string} />;
          case "segment":
            return <SegmentationName value={value as string} />;
          case "jobFunction":
            return <JobFunctionName value={value as string} />;
          case "licenseId":
            return <LicenseTypeName value={value as string} />;
          case "subIndustryId":
            return <SubIndustryName value={value as string} />;
          case "source":
            return <SourceName value={value as string} />;
          case "inboundCampaignType":
          case "outboundCampaignType":
            return <SourceTypeName value={value as string} />;
          case "referenceability":
            return <Referenceability value={value as string} />;
          case "aeConfidenceLevel":
            return <AEConfidenceLevel value={value as string} />;
          case "renewalType":
            return <RenewalTypeName value={Number(value)} />;
          case "onboardingStageId":
            return <OnboardingStageName value={value as string} />;
          case "arrMrr":
          case arrOrMrrField:
          case "dealValueAmount":
          case "annualContractValue":
          case "totalContractValue":
          case "contractTotalAmount":
            return <DealValueAmount value={Number(value)} />;
          default:
            break;
        }
        switch (fieldConfig?.displayType) {
          case FieldDisplayType.USERS:
          case FieldDisplayType.PEOPLE:
          case FieldDisplayType.PEOPLE_IN_COMPANY:
            return (
              <UserOrPersonBadge
                id={value as string}
                className="bg-transparent"
                textVariant="bodySecondary"
              />
            );
          case FieldDisplayType.COMPANIES:
            return <CompanyName value={value as string} />;

          case FieldDisplayType.PARTNERS:
            return <PartnerName value={value} />;

          case FieldDisplayType.PARTNER_CONTACTS:
            return <PartnerContactName value={value} />;

          case FieldDisplayType.PRODUCTS:
            return <ProductName value={value as string} />;

          case FieldDisplayType.DATE:
            return (
              <TextField>
                {simpleDate(toLocalDate(String(value)), "long")}
              </TextField>
            );
          case FieldDisplayType.CURRENCY:
            // Every field registered with displayType "currency" (singular) in
            // the fields registry is a money-amount field (arr, mrr, tcv, ...);
            // the actual currency-code field (deals.currency) uses the distinct
            // "currencies" (plural) enum value, so a numeric value here is always
            // an amount. The non-numeric branch is a defensive fallback only.
            return Number.isFinite(Number(value)) ? (
              <DealValueAmount value={Number(value)} />
            ) : (
              <Currency currencyName={value as string} />
            );

          default:
            return <TextField>{value}</TextField>;
        }
      },
      [fieldName, entityType, changeType, t, arrOrMrrField, fieldConfig]
    );

    return (
      delta &&
      delta.length === 2 && (
        <div className="flex items-center gap-2">
          {showPeopleDelta && isSingleChange ? null : (
            <Text>
              <HighlightedText
                text={fieldConfig?.label || fieldName}
                query={searchQuery}
              />
            </Text>
          )}
          {showPeopleDelta ? (
            <PeopleDelta
              peopleIdsDelta={peopleIdsDelta}
              isSingleChange={isSingleChange}
            />
          ) : (
            <>
              {renderActionValue(delta[0])}
              <IconContainer color="secondary">
                <ArrowRight />
              </IconContainer>
              {renderActionValue(delta[1])}
            </>
          )}
        </div>
      )
    );
  }
);
export default LogAction;
